import React, { useState, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Switch, Table } from 'antd';
import type { EmailSubscriptions } from '@owl-frontend/api-client';
import { Dialog } from '@owl-frontend/components';
import { Locale } from '../../context/AppContainer/AppContainer';
import styles from './SettingsModalComponent.module.scss';

interface SettingsModalComponentProps {
  open: boolean;
  handleOnClose: () => void;
  emailSubscriptions?: EmailSubscriptions;
  updateEmailSubscriptions: (emailSubscriptions: EmailSubscriptions) => void;
}

interface EmailSubscriptionRecord {
  key: string;
  title: string;
  description: string;
  isSubscribed: boolean;
}

const SettingsModalComponent: React.FC<SettingsModalComponentProps> = ({
  open,
  handleOnClose,
  emailSubscriptions: globalEmailSubscriptions,
  updateEmailSubscriptions,
}) => {
  const [emailSubscriptions, setEmailSubscriptions] =
    useState<EmailSubscriptions>(globalEmailSubscriptions || {});
  const { messages } = React.useContext(Locale);

  // eslint-disable-next-line react-etc/prefer-usememo
  useEffect(() => {
    setEmailSubscriptions(globalEmailSubscriptions || {});
  }, [globalEmailSubscriptions]);

  const debouncedUpdateEmailSubscriptions = useDebouncedCallback(
    (updatedSubscriptions: EmailSubscriptions) => {
      updateEmailSubscriptions(updatedSubscriptions);
    },
    500
  );

  const columns = [
    {
      title: messages['users.emailSettings.columns.title'],
      dataIndex: 'title',
      key: 'title',
      width: '250px',
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: messages['users.emailSettings.columns.description'],
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: messages['users.emailSettings.columns.subscribed'],
      dataIndex: 'isSubscribed',
      key: 'isSubscribed',
      render: (text: boolean, record: EmailSubscriptionRecord) => {
        const updatedEmailSubscriptions = {
          ...emailSubscriptions,
          [record.key]: { isSubscribed: !text },
        };
        return (
          <Switch
            checked={text}
            onChange={() => {
              setEmailSubscriptions(updatedEmailSubscriptions);
              debouncedUpdateEmailSubscriptions(updatedEmailSubscriptions);
            }}
          />
        );
      },
    },
  ];

  return (
    <Dialog
      data-testid="user-settings-dialog"
      title={messages['main.settings']}
      size="medium"
      open={open}
      onClose={handleOnClose}
      footer={{
        onConfirmOpts: {
          label: messages['shared.ok'],
          onClick: handleOnClose,
        },
      }}
    >
      <div>
        <h2 className={styles.sectionHeader}>
          {messages['users.emailSettings.title']}
        </h2>
        <Table
          dataSource={Object.keys(emailSubscriptions).map((key) => {
            return {
              key,
              title: messages[`users.emails.${key}.title`],
              description: messages[`users.emails.${key}.description`],
              isSubscribed: emailSubscriptions[key].isSubscribed,
            };
          })}
          columns={columns}
        />
      </div>
    </Dialog>
  );
};

export default SettingsModalComponent;
